import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';

import {
  type FilteredInvoicePaymentDetail,
  type FilteredInvoiceRow,
} from '~entities/payment/api/get-filtered-invoices';

function formatMoney(currencySymbol: string, value: number) {
  return `${currencySymbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export interface InvoiceExpandedRowProps {
  invoice: FilteredInvoiceRow;
  currencySymbol: string;
}

export function InvoiceExpandedRow({
  invoice,
  currencySymbol,
}: InvoiceExpandedRowProps) {
  const { formatInStudioTz } = useStudioDateFormatter();
  const payments: Array<FilteredInvoicePaymentDetail> = invoice.payments ?? [];
  const walletAmount = invoice.walletAmount;
  const transactionFee = invoice.transactionFeeAmount ?? 0;

  return (
    <div className='bg-muted/40 p-4'>
      <h4 className='mb-3 text-sm font-semibold text-foreground'>
        Payment details
      </h4>
      <div className='mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Invoice ID
          </span>
          <span className='break-all font-mono text-sm'>{invoice.id}</span>
        </div>
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Payment method
          </span>
          <span className='text-sm font-medium capitalize'>
            {invoice.paymentMethodSummary}
          </span>
        </div>
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Wallet deduction
          </span>
          <span className='text-sm font-medium'>
            {formatMoney(currencySymbol, walletAmount)}
          </span>
        </div>
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Transaction fee
          </span>
          <span className='text-sm font-medium'>
            {formatMoney(currencySymbol, transactionFee)}
          </span>
        </div>
      </div>
      {payments.length > 0 ? (
        <div className='space-y-3'>
          <p className='text-sm font-semibold text-foreground'>Breakdown</p>
          {payments.map((payment, idx) => (
            <div
              key={`${payment.paymentIntentId ?? ''}-${payment.date ?? ''}-${idx}`}
              className='rounded-md border border-border bg-card p-4'
            >
              <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                <div className='flex flex-col'>
                  <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    Method
                  </span>
                  <span className='mt-1 text-sm font-medium capitalize'>
                    {payment.method}
                  </span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    Amount
                  </span>
                  <span className='mt-1 text-sm font-semibold'>
                    {formatMoney(currencySymbol, payment.amount)}
                  </span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    Date
                  </span>
                  <span className='mt-1 text-sm'>
                    {payment.date
                      ? formatInStudioTz(payment.date, 'MMM d, yyyy h:mm a')
                      : '—'}
                  </span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    Payment intent ID
                  </span>
                  <span className='mt-1 break-all font-mono text-xs'>
                    {payment.paymentIntentId ?? '—'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
